import { Component, computed, ElementRef, HostListener, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { catchError, map, Observable, of, scan, startWith, switchMap } from 'rxjs';
import { GridModule } from '@progress/kendo-angular-grid';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import {
  PopoverAnchorDirective,
  PopoverBodyTemplateDirective,
  PopoverComponent,
  PopoverTitleTemplateDirective,
  TooltipDirective,
} from '@progress/kendo-angular-tooltip';
import { DatePipe } from '@progress/kendo-angular-intl';
import { Router } from '@angular/router';
import { CapitalizePipe } from '../../pipes';
import { ApplicationUserService, MercureService, UserNotificationService } from '../../services';
import { SHARED_ENVIRONMENT_CONFIG } from '../../config';
import { errorToast, HydraCollectionResponse, Notification, User } from '../../models';
import { isEmptyObject } from '../../utils';
import { ClickOutsideDirective, RcScrollHintsDirective } from '../../directives';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationService } from '@progress/kendo-angular-notification';

@Component({
  selector: 'rc-user-notifications',
  standalone: true,
  templateUrl: './user-notifications.component.html',
  styleUrl: './user-notifications.component.scss',
  imports: [
    ButtonComponent,
    GridModule,
    TranslateModule,
    CapitalizePipe,
    PopoverBodyTemplateDirective,
    PopoverComponent,
    PopoverAnchorDirective,
    DatePipe,
    RcScrollHintsDirective,
    CapitalizePipe,
    PopoverTitleTemplateDirective,
    TooltipDirective,
    ClickOutsideDirective,
    // TranslatePipe,
  ],
  // providers: [CapitalizePipe, TranslatePipe, KendoMessageService],
})
export class UserNotificationsComponent implements OnDestroy {
  @HostListener('window:resize')
  onResize() {
    this.recalcPopoverWidth();
  }

  environment = inject(SHARED_ENVIRONMENT_CONFIG);
  #applicationUserService = inject(ApplicationUserService);
  #notificationService = inject(NotificationService);
  #httpClient = inject(HttpClient);
  #mercureService = inject(MercureService<Notification>);
  #router = inject(Router);
  user = this.#applicationUserService.user$;
  readonly desiredWidth = 440;
  readonly minWidth = 366;

  popoverWidth = signal(this.desiredWidth);

  rawNotifications$ = this.user.pipe(
    switchMap((user) => {
      // remembered so ngOnDestroy can stop listening to this user's topic
      this.currentUser = user;
      if (user) {
        this.subscribeSubject = `/users/${user.id}/notifications`;

        this.#mercureService.subscribeToSpecificTopic(this.subscribeSubject);
        return this.#getNotifications();
      } else {
        return of([]);
      }
    }),
  );
  notifications = computed(() => {
    const list = this.rawNotifications();
    const optimistic = this.optimisticReadIds();
    return list.filter((n) => !optimistic.has(n['@id']!));
  });

  optimisticReadIds = signal(new Set<string>());

  rawNotifications = toSignal(this.rawNotifications$, { initialValue: [] as Notification[] });
  currentUser: User | null = null;
  notificationService = inject(UserNotificationService);
  subscribeSubject: string;
  totalItemsToRead = 0;

  notificationsPopoverAnchor = viewChild(PopoverAnchorDirective);
  bellBtn = viewChild<ElementRef<HTMLElement>>('bellBtn');

  #getNotifications(): Observable<Notification[]> {
    return this.#httpClient
      // every unread notification, not just the first page, so the badge and "mark all as read" cover them all
      .get<HydraCollectionResponse<Notification>>(`${this.environment.apiUrl}/notifications?isRead=false&pagination=false`)
      .pipe(
        map((result) => result['hydra:member']),
        // a failed load shows an empty list (instead of breaking the bell) and still listens to live updates
        catchError(() => of([] as Notification[])),
        switchMap((notifications) => {
          this.#mercureService.subscribeToSpecificTopic(this.subscribeSubject);
          return this.#getRealTimeUpdates(notifications);
        }),
      );
  }

  recalcPopoverWidth(): void {
    const anchorDir = this.bellBtn();
    const anchorEl = anchorDir?.nativeElement as HTMLElement | undefined;
    if (!anchorEl) return;

    const rect = anchorEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const viewportRight = window.innerWidth;

    // Max width that keeps the centered popover within the right edge
    const maxCenteredWidth = Math.max(0, 2 * (viewportRight - cx));

    // Final width: respect desiredWidth but don’t overflow; keep a sensible floor
    const w = Math.max(this.minWidth, Math.min(this.desiredWidth, Math.floor(maxCenteredWidth)));
    this.popoverWidth.set(w);
  }

  #getRealTimeUpdates(initialData: Notification[]): Observable<Notification[]> {
    return this.#mercureService.getCustomTopicUpdates(this.subscribeSubject).pipe(
      startWith(null),
      scan((currentData, update) => {
        if (!update || isEmptyObject(update)) return currentData;

        const existingIndex = currentData.findIndex((n) => n['@id'] === update['@id']);

        let newData = [...currentData];

        if (existingIndex !== -1) {
          if (update.isRead) {
            newData.splice(existingIndex, 1);
          } else {
            newData[existingIndex] = update;
          }
        } else {
          if (!update.isRead) {
            newData = [update, ...newData];
          }
        }

        this.totalItemsToRead = newData.length;
        return newData;
      }, initialData),
    );
  }

  ngOnDestroy(): void {
    if (this.currentUser) {
      this.#mercureService.unsubscribeFromCustomTopics([`/users/${this.currentUser.id}/notifications`]);
    }
  }

  markAllAsRead() {
    const ids = this.notifications().map((n) => n['@id']!);

    this.notificationService.markCurrentAsRead({ notifications: ids }).subscribe({
      next: () => {
        this.optimisticReadIds.update((s) => new Set([...s, ...ids]));
      },
      error: (err) => {
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) {
          notification.content.instance.setCurrentValue(err);
        }
      },
    });
  }

  markNotificationAsRead(n: Notification) {
    this.notificationService.markRead(n).subscribe({
      next: () => {
        const id = n['@id']!;
        this.optimisticReadIds.update((s) => new Set([...s, id]));
      },
      error: (err) => {
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) {
          notification.content.instance.setCurrentValue(err);
        }
      },
    });
  }

  openReferral(notification: Notification) {
    const isComplaint = notification.referralPath.includes('complaint');
    const url = isComplaint ? `/documents${notification.referralPath}/messages` : notification.referralPath;
    this.#router.navigate([url]);
    this.markNotificationAsRead(notification);
    this.notificationsPopoverAnchor()?.hide();
  }
}
